import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { PaletteMode } from '@mui/material/styles';

export type ThemeMode = PaletteMode | 'system';

function resolveMode(themeMode: ThemeMode): PaletteMode {
  if (themeMode !== 'system') return themeMode;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemePrefsState {
  themeMode: ThemeMode;
  resolvedMode: PaletteMode;
  setThemeMode: (mode: ThemeMode) => void;
  cycleThemeMode: () => void;
}

const CYCLE_ORDER: ThemeMode[] = ['light', 'dark', 'system'];

export const useThemePrefsStore = create<ThemePrefsState>()(
  persist(
    (set, get) => ({
      themeMode: 'system',
      resolvedMode: resolveMode('system'),
      setThemeMode: (mode) => set({ themeMode: mode, resolvedMode: resolveMode(mode) }),
      cycleThemeMode: () => {
        const current = get().themeMode ?? 'system';
        const idx = CYCLE_ORDER.indexOf(current);
        const next: ThemeMode = CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length] ?? 'system';
        set({ themeMode: next, resolvedMode: resolveMode(next) });
      },
    }),
    {
      name: 'equip_theme_prefs',
      storage: createJSONStorage(() =>
        typeof window !== 'undefined' ? window.localStorage : {
          getItem: () => null,
          setItem: () => {},
          removeItem: () => {},
        },
      ),
      partialize: (state) => ({ themeMode: state.themeMode }),
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<ThemePrefsState>) };
        merged.resolvedMode = resolveMode(merged.themeMode ?? 'system');
        return merged;
      },
    },
  ),
);

// Keep resolvedMode in sync whenever themeMode changes (including rehydration)
useThemePrefsStore.subscribe((state) => {
  const correct = resolveMode(state.themeMode ?? 'system');
  if (state.resolvedMode !== correct) {
    useThemePrefsStore.setState({ resolvedMode: correct });
  }
});

// Listen for system color-scheme changes when mode is 'system'
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { themeMode } = useThemePrefsStore.getState();
    if (themeMode === 'system') {
      useThemePrefsStore.setState({ resolvedMode: resolveMode('system') });
    }
  });
}
